import { Database, Json } from '@/database.types';
import { validateOrCreateProductUser } from '@/store/configSlice';
import { getSupabaseClient, SupabaseConfig } from '@/utils/supabase';
import { v4 as uuidv4 } from 'uuid';
import { OnboardingStep } from '@/store/onboardingSlice';

type StepResponseEntry = {
	key: string; // Unique step ID or key
	index: number; // Step order
	type: OnboardingStep['type'];
	value: OnboardingStep['value'] | null;
	completed_at: string | null;
};

type StepResponses = StepResponseEntry[];

type OnboardingConfig =
	Database['public']['Tables']['onboarding_configs']['Row'];
type OnboardingLog = Database['public']['Tables']['onboarding_logs']['Row'];

export const fetchOnboardingFlows = async (
	productId: string,
	config?: SupabaseConfig
): Promise<{ onboardings: OnboardingConfig[] | null; error: Error | null }> => {
	const client = getSupabaseClient(config);

	try {
		const { data, error } = await client
			.from('onboarding_configs')
			.select('*')
			.eq('product_id', productId)
			.eq('is_active', true)
			.order('created_at', { ascending: true });

		if (error) {
			console.error('Error fetching onboarding configs:', error);
			return { onboardings: null, error };
		}

		return { onboardings: data as OnboardingConfig[], error: null };
	} catch (err) {
		console.error('Error fetching onboarding configs:', err);
		return { onboardings: null, error: err as Error };
	}
};

export const createOnboardingLog = async (
	userId: string,
	productId: string,
	onboardingConfigId?: string,
	config?: SupabaseConfig
): Promise<{ log: OnboardingLog | null; error: Error | null }> => {
	const client = getSupabaseClient(config);

	await validateOrCreateProductUser(userId, productId);

	let configId = onboardingConfigId;
	if (!configId) {
		const { onboardings } = await fetchOnboardingFlows(productId);
		configId = onboardings?.[0]?.id;
	}

	const logData = {
		id: uuidv4(),
		onboarding_config_id: configId,
		product_user_id: userId,
		product_id: productId,
		started_at: new Date().toISOString(),
	};

	try {
		const { data, error } = await client
			.from('onboarding_logs')
			.insert(logData)
			.select()
			.single();

		if (error) {
			console.error('Error creating onboarding log:', error);
			return { log: null, error };
		}

		return { log: data as OnboardingLog, error: null };
	} catch (err) {
		console.error('Error creating onboarding log:', err);
		return { log: null, error: err as Error };
	}
};

export const updateOnboardingLog = async (
	logId: string,
	updates: {
		onboardingSteps?: OnboardingStep[];
		completed?: boolean;
	},
	config?: SupabaseConfig
): Promise<{ log: OnboardingLog | null; error: Error | null }> => {
	const client = getSupabaseClient(config);

	try {
		// Build structured array from onboardingSteps
		let stepResponses: StepResponses | null = null;
		if (updates.onboardingSteps) {
			stepResponses = updates.onboardingSteps.map((step, index) => ({
				key: step.id ?? `step_${index}`,
				index,
				type: step.type,
				value: step.value ?? null,
				completed_at: step.isCompleted ? new Date().toISOString() : null,
			}));
		}

		const updateData: {
			step_responses: Json | null;
			completed_at?: string;
		} = {
			step_responses: stepResponses,
		};

		if (updates.completed) {
			updateData.completed_at = new Date().toISOString();
		}

		const { data, error } = await client
			.from('onboarding_logs')
			.update(updateData)
			.eq('id', logId)
			.select()
			.single();

		if (error) {
			console.error('Error updating onboarding log:', error);
			return { log: null, error };
		}

		return { log: data as OnboardingLog, error: null };
	} catch (err) {
		console.error('Error updating onboarding log:', err);
		return { log: null, error: err as Error };
	}
};
